import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: "/api/v1",
});

let refreshInFlight: Promise<string | null> | null = null;

const tryRefreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem("refreshToken") || "";
  const gatewaySession = localStorage.getItem("gatewaySession") === "1";
  if (!refreshToken && gatewaySession) {
    return axios
      .post("/api/v1/auth/refresh", {}, { withCredentials: true })
      .then((res) => (res.status === 200 ? "__gateway_session__" : null))
      .catch(() => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("gatewaySession");
        return null;
      });
  }
  if (!refreshToken) return null;
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post("/api/v1/auth/refresh", { refresh_token: refreshToken })
      .then((res) => {
        const token = String(res.data?.access_token || "");
        const nextRefresh = String(res.data?.refresh_token || "");
        if (token) localStorage.setItem("accessToken", token);
        if (nextRefresh) localStorage.setItem("refreshToken", nextRefresh);
        return token || null;
      })
      .catch(() => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("gatewaySession");
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem("accessToken") || "";
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retry && !String(original.url || "").includes("/auth/")) {
      original._retry = true;
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) {
        if (refreshed !== "__gateway_session__") {
          original.headers.Authorization = `Bearer ${refreshed}`;
        }
        return axiosInstance(original);
      }
    }
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("gatewaySession");
    window.location.href = "/login";
    return Promise.reject(error);
  }
);

export type PromptExampleBucket = "standard" | "team" | "idb" | "idb_team" | "umbrex" | "umbrex_team";

export type WorkspaceAssetsResponse = {
  status: string;
  assets: {
    catalant_project_experience_pdf_path: string;
    catalant_project_tool_mode_enabled: boolean;
    umbrex_dc_bio?: string;
    umbrex_dc_expert_id?: string;
  };
};

export type LearnedShorthandEntry = {
  code: string;
  meaning: string;
  family_key: string;
  search_text: string;
  seen?: number;
  sources?: string[];
  updated_at?: string;
};

export type LearnedShorthandResponse = {
  status: string;
  count: number;
  entries: LearnedShorthandEntry[];
};

export type ExpertCatalogItem = {
  expert_id: string;
  name: string;
  expert_type?: string;
  summary?: string;
  created_at?: string;
};

export type ExpertCatalogResponse = {
  status: string;
  message?: string;
  source_path?: string;
  expert_count: number;
  experts: ExpertCatalogItem[];
  dc_expert_id_setting?: string;
  dc_expert?: ExpertCatalogItem | null;
};

export type AgentMemoryItem = {
  id: number;
  agent_slug: string;
  platform_type: string;
  memory_type: string;
  memory_text: string;
  content: Record<string, unknown>;
  confidence?: number | null;
  source_rfp_id?: number | null;
  source_step_number?: number | null;
  source_feedback_id?: number | null;
  source_agent_run_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  status: string;
};

export type AgentMemoriesResponse = {
  status: string;
  count: number;
  limit: number;
  offset: number;
  memories: AgentMemoryItem[];
};

export type PromptTextExample = {
  id: string;
  title: string;
  text: string;
  enabled: boolean;
  order: number;
};

export type PromptExamplesResponse = {
  status: string;
  bucket: PromptExampleBucket;
  examples: PromptTextExample[];
  mapped_agents: string[];
  updated_at?: string;
  updated_by?: string;
};

export type DevLogItem = {
  type: string;
  id: number;
  agent_name?: string;
  session_id?: string;
  status?: string;
  input_status?: string;
  created_at: string;
  payload_preview?: unknown;
  response_preview?: unknown;
};

export type DevLogsResponse = {
  status: string;
  count: number;
  limit: number;
  offset: number;
  logs: DevLogItem[];
};

export type AgentDiagnosticsRun = {
  run_id: string;
  state: string;
  trigger_reason: string;
  created_at: string | null;
  updated_at: string | null;
  reflection_ids: string[];
};

export type AgentDiagnosticsMemory = {
  memory_id: string;
  created_at: string | null;
  confidence: number;
  tags: string[];
  derived_from: string[];
  content: string;
};

export type AgentDiagnosticsPoolItem = {
  item_id: string;
  session_id: string;
  state: string;
  created_at: string | null;
  reason: string;
};

export type AgentDiagnosticsResponse = {
  status: string;
  agent_slug: string;
  global: {
    flame: {
      is_triggered: boolean;
      trigger_reason: string;
      pending_pool_items: number;
      last_run: AgentDiagnosticsRun | null;
    };
    runs: {
      total: number;
      success_count: number;
      failed_count: number;
      latest: AgentDiagnosticsRun[];
    };
    pool: {
      total_count: number;
      pending_count: number;
      latest: AgentDiagnosticsPoolItem[];
    };
    memories: {
      reflection_count: number;
      temp_count: number;
      latest_reflections: AgentDiagnosticsMemory[];
      latest_temp: AgentDiagnosticsMemory[];
    };
  };
  session?: {
    session_id: string;
    pool_pending_count: number;
    reflection_memory_count: number;
    temp_memory_count: number;
    latest_reflection: AgentDiagnosticsMemory | null;
    latest_temp_memory: AgentDiagnosticsMemory | null;
    latest_pool_items: AgentDiagnosticsPoolItem[];
  };
};

export const apiClient = {
  getWorkspaceAssets: () =>
    axiosInstance.get("/workspace/assets").then((res) => res.data as WorkspaceAssetsResponse),

  getExpertCatalog: () =>
    axiosInstance.get("/workspace/assets/experts").then((res) => res.data as ExpertCatalogResponse),

  getLearnedShorthand: () =>
    axiosInstance.get("/workspace/assets/umbrex-shorthand").then((res) => res.data as LearnedShorthandResponse),

  deleteLearnedShorthand: (entry: { code: string; family_key: string }) =>
    axiosInstance
      .delete("/workspace/assets/umbrex-shorthand", { params: entry })
      .then((res) => res.data as LearnedShorthandResponse),

  updateWorkspaceAssets: (payload: {
    catalant_project_tool_mode_enabled?: boolean;
    umbrex_dc_bio?: string;
    umbrex_dc_expert_id?: string;
  }) =>
    axiosInstance.patch("/workspace/assets", payload).then((res) => res.data as WorkspaceAssetsResponse),

  uploadCatalantProjectPdf: (file: File, replace: boolean = false) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("replace", String(replace));
    return axiosInstance
      .post("/workspace/assets/catalant-project-experience/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  },

  getAgentMemories: (filters: { agent_slug?: string; platform_type?: string; limit?: number; offset?: number } = {}) =>
    axiosInstance
      .get("/workspace/agent-memories", {
        params: {
          agent_slug: filters.agent_slug,
          platform_type: filters.platform_type,
          limit: filters.limit ?? 200,
          offset: filters.offset ?? 0,
        },
      })
      .then((res) => res.data as AgentMemoriesResponse),

  deleteAgentMemory: (memoryId: number) =>
    axiosInstance
      .delete(`/workspace/agent-memories/${memoryId}`)
      .then((res) => res.data as { status: string; memory_id: number; deactivated: boolean }),

  getPromptExamples: (bucket: PromptExampleBucket) =>
    axiosInstance
      .get("/workspace/prompt-examples", { params: { bucket } })
      .then((res) => res.data as PromptExamplesResponse),

  replacePromptExamples: (bucket: PromptExampleBucket, examples: PromptTextExample[]) =>
    axiosInstance
      .put(`/workspace/prompt-examples/${bucket}`, { examples })
      .then((res) => res.data as PromptExamplesResponse),

  addPromptExample: (bucket: PromptExampleBucket, example: Omit<PromptTextExample, "id"> & { id?: string }) =>
    axiosInstance
      .post(`/workspace/prompt-examples/${bucket}`, { example })
      .then((res) => res.data as PromptExamplesResponse),

  patchPromptExample: (bucket: PromptExampleBucket, example_id: string, patch: Partial<PromptTextExample>) =>
    axiosInstance
      .patch(`/workspace/prompt-examples/${bucket}/${example_id}`, patch)
      .then((res) => res.data as PromptExamplesResponse),

  deletePromptExample: (bucket: PromptExampleBucket, example_id: string) =>
    axiosInstance
      .delete(`/workspace/prompt-examples/${bucket}/${example_id}`)
      .then((res) => res.data as PromptExamplesResponse),

  reseedPromptExamples: (bucket: PromptExampleBucket) =>
    axiosInstance
      .post(`/workspace/prompt-examples/${bucket}/reseed-defaults`)
      .then((res) => res.data as PromptExamplesResponse),

  getDevAgents: () => axiosInstance.get("/dev/agents").then((res) => res.data as { status: string; agents: string[] }),

  getDevAgentDiagnostics: (agent_slug: string, session_id?: string, limit: number = 20) =>
    axiosInstance
      .get(`/dev/diagnostics/${agent_slug}`, { params: { session_id, limit } })
      .then((res) => res.data as AgentDiagnosticsResponse),

  getDevLogs: (filters: { log_type?: string; agent_name?: string; status?: string }, limit = 50, offset = 0) =>
    axiosInstance.get("/dev/logs", { params: { ...filters, limit, offset } }).then((res) => res.data as DevLogsResponse),
};

export default apiClient;
